import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { asyncNoop, noop } from 'vet-bones/utils';

import { MAX_COMPETENCY_QUESTIONS } from 'src/constants/limits';
import { CompetencyQuestion } from 'src/constants/voicebox';
import { VirtualGraphSourceConfig } from 'src/containers/CreateProjectOverlay/types';
import { hasPendingAutoGenerateMockDataSelector } from 'src/selectors/pendingAutoGenerateMockData';
import { hasPendingAutoGenerateSelector } from 'src/selectors/pendingAutoGenerateModel';
import { uiSlice } from 'src/store/ui/ui.slice';
import { FileCreationMetadata } from 'src/types/fileUpload';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useGenerateQuestions } from 'src/utils/voicebox/hooks/useGenerateQuestions';

export enum CreateProjectStepValue {
  PROJECT_SCOPING = 'PROJECT_SCOPING',
  COMPETENCY_QUESTIONS = 'COMPETENCY_QUESTIONS',
  DATA_CONNECTION = 'DATA_CONNECTION',
}

export type GeneratedCompetencyQuestion = CompetencyQuestion & {
  isGenerated?: boolean;
};

export type CreateProjectContextValue = {
  currentStep: CreateProjectStepValue;
  name: string;
  description: string;
  competencyQuestions: GeneratedCompetencyQuestion[];
  didGenerateInitialQuestions: boolean;
  isGeneratingQuestions: boolean;
  isAutoGeneratingModel: boolean;
  isAutoGeneratingMockData: boolean;
  selectedFiles: File[];
  fileMetadata: Record<number, FileCreationMetadata>;
  virtualGraphConfigs: VirtualGraphSourceConfig[];
  handleBack: () => void;
  handleGenerateQuestions: () => Promise<void>;
  resetState: () => void;
  setCurrentStep: React.Dispatch<React.SetStateAction<CreateProjectStepValue>>;
  setName: React.Dispatch<React.SetStateAction<string>>;
  setDescription: React.Dispatch<React.SetStateAction<string>>;
  setCompetencyQuestions: React.Dispatch<
    React.SetStateAction<GeneratedCompetencyQuestion[]>
  >;
  setSelectedFiles: React.Dispatch<React.SetStateAction<File[]>>;
  setFileMetadata: React.Dispatch<
    React.SetStateAction<Record<number, FileCreationMetadata>>
  >;
  setIsAutoGeneratingModel: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAutoGeneratingMockData: React.Dispatch<React.SetStateAction<boolean>>;
  setVirtualGraphConfigs: React.Dispatch<
    React.SetStateAction<VirtualGraphSourceConfig[]>
  >;
};

export const CreateProjectContext =
  React.createContext<CreateProjectContextValue>({
    currentStep: CreateProjectStepValue.PROJECT_SCOPING,
    name: '',
    description: '',
    competencyQuestions: [],
    didGenerateInitialQuestions: false,
    isGeneratingQuestions: false,
    isAutoGeneratingModel: false,
    isAutoGeneratingMockData: false,
    selectedFiles: [],
    fileMetadata: {},
    virtualGraphConfigs: [],
    handleBack: noop,
    handleGenerateQuestions: asyncNoop,
    resetState: noop,
    setCurrentStep: noop,
    setName: noop,
    setDescription: noop,
    setCompetencyQuestions: noop,
    setSelectedFiles: noop,
    setFileMetadata: noop,
    setIsAutoGeneratingModel: noop,
    setIsAutoGeneratingMockData: noop,
    setVirtualGraphConfigs: noop,
  });

export const useCreateProjectState = () => {
  return React.useContext(CreateProjectContext);
};

export const useCreateProjectStateValue = ({
  step: initialStep,
  name: initialName,
  description: initialDescription,
  competencyQuestions: initialCompetencyQuestions,
  didGenerateInitialQuestions: initialDidGenerateInitialQuestions,
  isGeneratingQuestions: initialIsGeneratingQuestions,
}: {
  step?: CreateProjectStepValue;
  name?: string;
  description?: string;
  competencyQuestions?: GeneratedCompetencyQuestion[];
  didGenerateInitialQuestions?: boolean;
  isGeneratingQuestions?: boolean;
} = {}): CreateProjectContextValue => {
  const dispatch = useReduxDispatch();

  const pendingAutoGenerate = useReduxSelector(hasPendingAutoGenerateSelector);
  const pendingAutoGenerateMockData = useReduxSelector(
    hasPendingAutoGenerateMockDataSelector
  );

  const [currentStep, setCurrentStep] = React.useState(
    pendingAutoGenerate || pendingAutoGenerateMockData
      ? CreateProjectStepValue.DATA_CONNECTION
      : initialStep || CreateProjectStepValue.PROJECT_SCOPING
  );

  const [isAutoGeneratingModel, setIsAutoGeneratingModel] =
    React.useState(pendingAutoGenerate);
  const [isAutoGeneratingMockData, setIsAutoGeneratingMockData] =
    React.useState(pendingAutoGenerateMockData);
  const [name, setNameFn] = React.useState(initialName || '');
  const [description, setDescriptionFn] = React.useState(
    initialDescription || ''
  );
  const [competencyQuestions, setCompetencyQuestionsFn] = React.useState(
    initialCompetencyQuestions || [{ id: uuid(), question: '' }]
  );

  const [didGenerateInitialQuestions, setDidGenerateInitialQuestions] =
    React.useState(initialDidGenerateInitialQuestions ?? false);
  const [isGeneratingQuestions, setIsGeneratingQuestions] = React.useState(
    initialIsGeneratingQuestions ?? false
  );

  const [selectedFiles, setSelectedFiles] = React.useState<File[]>([]);
  const [fileMetadata, setFileMetadata] = React.useState<
    Record<number, FileCreationMetadata>
  >({});
  const [virtualGraphConfigs, setVirtualGraphConfigs] = React.useState<
    VirtualGraphSourceConfig[]
  >([]);

  const setCompetencyQuestions = React.useCallback<
    React.Dispatch<React.SetStateAction<GeneratedCompetencyQuestion[]>>
  >((questionsOrFn) => {
    setCompetencyQuestionsFn((prevQuestions) => {
      const newQuestions =
        typeof questionsOrFn === 'function'
          ? questionsOrFn(prevQuestions)
          : questionsOrFn;
      return newQuestions.slice(0, MAX_COMPETENCY_QUESTIONS);
    });
  }, []);

  const resetState = React.useCallback(() => {
    setNameFn('');
    setDescriptionFn('');
    setCompetencyQuestions([{ id: uuid(), question: '' }]);
    setDidGenerateInitialQuestions(false);
    setIsGeneratingQuestions(false);
    setIsAutoGeneratingModel(false);
    setIsAutoGeneratingMockData(false);
    setSelectedFiles([]);
    setFileMetadata({});
    setVirtualGraphConfigs([]);
  }, [setCompetencyQuestions]);

  const handleBack = React.useCallback(() => {
    dispatch(uiSlice.actions.toggledCreateProjectOverlay({ isOpen: false }));
    resetState();
  }, [dispatch, resetState]);

  const generateQuestions = useGenerateQuestions();

  const handleGenerateQuestions = React.useCallback(async () => {
    if (isGeneratingQuestions) {
      return;
    }

    setIsGeneratingQuestions(true);

    const questions = await generateQuestions(name, description);
    if (questions) {
      setCompetencyQuestions((prevQuestions) => {
        const nextQuestions = prevQuestions.filter(
          (question) => question.question
        );
        const questionsSet = new Set(nextQuestions.map((q) => q.question));
        questions.forEach((question) => {
          if (!questionsSet.has(question.question)) {
            nextQuestions.push({
              id: question.id,
              question: question.question,
              isGenerated: true,
            });
          }
        });
        return nextQuestions;
      });
    }

    setDidGenerateInitialQuestions(true);
    setIsGeneratingQuestions(false);
  }, [
    description,
    generateQuestions,
    isGeneratingQuestions,
    name,
    setCompetencyQuestions,
  ]);

  return {
    currentStep,
    name,
    description,
    competencyQuestions,
    didGenerateInitialQuestions,
    isGeneratingQuestions,
    isAutoGeneratingModel,
    isAutoGeneratingMockData,
    selectedFiles,
    fileMetadata,
    virtualGraphConfigs,
    handleBack,
    handleGenerateQuestions,
    resetState,
    setCurrentStep,
    setName: setNameFn,
    setDescription: setDescriptionFn,
    setCompetencyQuestions,
    setSelectedFiles,
    setFileMetadata,
    setIsAutoGeneratingModel,
    setIsAutoGeneratingMockData,
    setVirtualGraphConfigs,
  };
};
